import React, { useEffect, useState } from 'react';
import { CheckCircle2Icon, KeyRoundIcon, LockKeyholeIcon, MailIcon, MenuIcon, PoundSterlingIcon, UserRoundIcon } from 'lucide-react';
import { Sidebar } from '../components/Sidebar';
import { settingsActions, useSettingsStore } from '../data/settingsStore';
import { useScreenInit } from '../useScreenInit';
const CURRENT_USER_ID = 'user-matthew';
export function Account() {
  useScreenInit();
  const {
    users
  } = useSettingsStore();
  const user = users.find(item => item.id === CURRENT_USER_ID);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [hourlyCost, setHourlyCost] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  useEffect(() => {
    if (!user) return;
    setFirstName(user.firstName);
    setLastName(user.lastName);
    setEmail(user.email);
    setHourlyCost(user.hourlyCost.toString());
  }, [user]);
  if (!user) {
    return <AccountShell sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} data-id="element-557" data-mp="31w30wzew7ibu"><div className="mx-auto max-w-md rounded-2xl border border-rose-200 bg-rose-50 p-6 text-center" data-id="element-558" data-mp="2x3jc16ar90rq"><h1 className="text-lg font-semibold text-rose-900" data-id="element-559" data-mp="2t3smf5wysqos">Account unavailable</h1><p className="mt-2 text-sm text-rose-700" data-id="element-560" data-mp="2o3mvjryir87r">Your profile could not be found. Please contact an administrator.</p></div></AccountShell>;
  }
  const handleSave = (event: React.FormEvent) => {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (!firstName.trim()) nextErrors.firstName = 'Enter your first name.';
    if (!lastName.trim()) nextErrors.lastName = 'Enter your last name.';
    if (!email.trim()) nextErrors.email = 'Enter your email address.';else if (!/^\S+@\S+\.\S+$/.test(email)) nextErrors.email = 'Enter a valid email address.';
    if (!hourlyCost || Number.isNaN(Number(hourlyCost)) || Number(hourlyCost) < 0) nextErrors.hourlyCost = 'Enter a valid hourly rate.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    settingsActions.updateUser(CURRENT_USER_ID, {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      hourlyCost: Number(hourlyCost)
    });
    setSaved(true);
    window.setTimeout(() => setSaved(false), 3500);
  };
  const clearError = (field: string) => {
    setErrors(current => {
      if (!current[field]) return current;
      const next = {
        ...current
      };
      delete next[field];
      return next;
    });
  };
  return <AccountShell sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} data-id="element-561" data-mp="2bnepp9ivmqhc">
      <div className="flex h-full min-w-0 flex-1 flex-col overflow-hidden bg-white md:rounded-2xl md:shadow-xl" data-id="element-562" data-mp="2giu1lvxbqcb4">
        <header className="border-b border-slate-100 px-5 py-5 md:px-8 md:py-7" data-id="element-563" data-mp="104u8p4wwbmxd">
          <button type="button" onClick={() => setSidebarOpen(true)} className="mb-4 flex items-center gap-2 text-[#14466B] md:hidden" aria-label="Open menu" data-id="element-564" data-mp="156ta4992s9ro"><MenuIcon className="h-6 w-6" data-id="element-565" data-mp="smmhe03tpazj" /></button>
          <div className="flex items-start gap-3" data-id="element-566" data-mp="sm31eoxqgwo5">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-[#14466B]" data-id="element-567" data-mp="16m5dtvn9uxw0"><UserRoundIcon className="h-5 w-5" data-id="element-568" data-mp="11nkfk1rkyoa7" /></span>
            <div data-id="element-569" data-mp="11o3wpg9ep8n6">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-570" data-mp="11lhpxiucax60">Your account</h1>
              <p className="mt-1 text-sm text-slate-500" data-id="element-571" data-mp="dxzv1efs3iyt">Manage your personal details, hourly rate, and sign-in security.</p>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-auto px-5 py-6 md:px-8 md:py-7" data-id="element-572" data-mp="nokypgab7dol">
          <div className="mx-auto grid max-w-4xl gap-6 lg:grid-cols-[minmax(0,1fr)_280px]" data-id="element-573" data-mp="iwb25heovfdc">
            <form onSubmit={handleSave} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-id="element-574" data-mp="3rccv5n18du52">
              <div className="border-b border-slate-100 px-5 py-4 md:px-6" data-id="element-575" data-mp="3mci8xwsehsqc">
                <h2 className="text-base font-semibold text-slate-900" data-id="element-576" data-mp="2pmogcb1faj5w">Profile details</h2>
                <p className="mt-1 text-sm text-slate-500" data-id="element-577" data-mp="2ukzpejjw8pn2">These details are used across your time entries and account experience.</p>
              </div>
              <div className="grid gap-5 p-5 md:grid-cols-2 md:p-6" data-id="element-578" data-mp="2yl9snogax1y8">
                <Field label="First name" error={errors.firstName} required data-id="element-579" data-mp="33dtch35l0tbh">
                  <input value={firstName} onChange={event => {
                  setFirstName(event.target.value);
                  clearError('firstName');
                }} className={inputClass(Boolean(errors.firstName))} autoComplete="given-name" data-id="element-580" data-mp="38cwcg96pg9cl" />
                </Field>
                <Field label="Last name" error={errors.lastName} required data-id="element-581" data-mp="3ibvfnc8pbcs7">
                  <input value={lastName} onChange={event => {
                  setLastName(event.target.value);
                  clearError('lastName');
                }} className={inputClass(Boolean(errors.lastName))} autoComplete="family-name" data-id="element-582" data-mp="3mcxehryphmtn" />
                </Field>
                <Field label="Email address" error={errors.email} required className="md:col-span-2" data-id="element-583" data-mp="1rz5xg7e1s0o6">
                  <div className="relative" data-id="element-584" data-mp="1kfn71g2h555k"><MailIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" data-id="element-585" data-mp="1kgybzj7noiy9" /><input type="email" value={email} onChange={event => {
                    setEmail(event.target.value);
                    clearError('email');
                  }} className={`${inputClass(Boolean(errors.email))} pl-9`} autoComplete="email" data-id="element-586" data-mp="1fm2a7rcmah7y" /></div>
                </Field>
                <Field label="Hourly rate" error={errors.hourlyCost} required data-id="element-587" data-mp="23e98iw1wkya0">
                  <div className="relative" data-id="element-588" data-mp="1yemuwli5xg0o"><PoundSterlingIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" data-id="element-589" data-mp="1yeeisvtv6eqj" /><input type="number" min="0" step="0.01" inputMode="decimal" value={hourlyCost} onChange={event => {
                    setHourlyCost(event.target.value);
                    clearError('hourlyCost');
                  }} className={`${inputClass(Boolean(errors.hourlyCost))} pl-9`} data-id="element-590" data-mp="1ubzx83yt90vk" /></div>
                  <span className="mt-1.5 block text-xs font-normal text-slate-500" data-id="element-591" data-mp="dyt1bm7jhmmr">Applied to new time logged against jobs. Existing entries retain their saved rate.</span>
                </Field>
              </div>
              <div className="flex flex-col-reverse gap-3 border-t border-slate-100 bg-slate-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between md:px-6" data-id="element-592" data-mp="ketbn2mg706f">
                <span className="min-h-5 text-sm text-emerald-700" role="status" data-id="element-593" data-mp="6hcyz9dk6a68">{saved && <span className="inline-flex items-center gap-1.5" data-id="element-594" data-mp="afkl6jqsk3ot"><CheckCircle2Icon className="h-4 w-4" data-id="element-595" data-mp="3sv4u8uq4hk3b" />Account details saved</span>}</span>
                <button type="submit" className="rounded-lg bg-[#14466B] px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#0F3D5C] focus:outline-none focus:ring-2 focus:ring-blue-200 focus:ring-offset-2" data-id="element-596" data-mp="1g753885wgv4">Save changes</button>
              </div>
            </form>

            <section className="h-fit overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-id="element-597" data-mp="3ntfkdiemzcgt">
              <div className="border-b border-slate-100 px-5 py-4" data-id="element-598" data-mp="3euu2zqkwi3hm">
                <div className="flex items-center gap-2" data-id="element-599" data-mp="3ex6gnrjmlrov"><LockKeyholeIcon className="h-4 w-4 text-[#14466B]" data-id="element-600" data-mp="2v8s5c7ubuwtc" /><h2 className="text-base font-semibold text-slate-900" data-id="element-601" data-mp="2v9k2vo7hj4m9">Password</h2></div>
                <p className="mt-1 text-sm text-slate-500" data-id="element-602" data-mp="2qbs3680lme7i">Keep your account secure.</p>
              </div>
              <div className="p-5" data-id="element-603" data-mp="34bb8kt20zbg4">
                <p className="text-sm leading-6 text-slate-600" data-id="element-604" data-mp="308mvuaa6xkdv">We’ll email a secure password-reset link to your current account email.</p>
                <button type="button" onClick={() => setResetSent(true)} className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-[#14466B]/25 bg-white px-4 py-2.5 text-sm font-medium text-[#14466B] transition-colors hover:bg-[#14466B]/[0.04] focus:outline-none focus:ring-2 focus:ring-blue-100" data-id="element-605" data-mp="3e6v0739k3nz2"><KeyRoundIcon className="h-4 w-4" data-id="element-606" data-mp="3izo63ugrh2cq" />Reset password</button>
                {resetSent && <p className="mt-3 flex items-start gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-xs leading-5 text-emerald-800" role="status" data-id="element-607" data-mp="tt0ckicc2f6"><CheckCircle2Icon className="mt-0.5 h-4 w-4 flex-shrink-0" data-id="element-608" data-mp="3rxhriaczmbl4" />Password reset instructions have been sent to {email}.</p>}
              </div>
            </section>
          </div>
        </main>
      </div>
    </AccountShell>;
}
function AccountShell({
  sidebarOpen,
  setSidebarOpen,
  children
}: {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  children: React.ReactNode;
}) {
  return <div className="flex h-screen w-screen overflow-hidden font-sans" style={{
    background: 'linear-gradient(to bottom, #16486B 0%, #0F3D5C 45%, #082A40 100%)'
  }} data-id="element-609" data-mp="1h41442i77pq6"><Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} data-id="element-610" data-mp="oij5lfilvq56" /><main className="flex min-w-0 flex-1 overflow-hidden p-0 md:py-3 md:pr-3" data-id="element-611" data-mp="tgat7xc7wq3k">{children}</main></div>;
}
function Field({
  label,
  error,
  required,
  className = '',
  children
}: {
  label: string;
  error?: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return <label className={`block text-sm font-medium text-slate-700 ${className}`} data-id="element-612" data-mp="5r8eoycvrdda">{label} {required && <span className="text-rose-600" data-id="element-613" data-mp="3ud3eee5wje8c">*</span>}<span className="mt-1.5 block" data-id="element-614" data-mp="3uc25af8g51t9">{children}</span>{error && <span className="mt-1 block text-xs font-normal text-rose-600" data-id="element-615" data-mp="3pe0knpvdgufo">{error}</span>}</label>;
}
function inputClass(hasError: boolean) {
  return `w-full rounded-lg border bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition-colors focus:ring-2 ${hasError ? 'border-rose-300 focus:border-rose-400 focus:ring-rose-100' : 'border-slate-200 focus:border-blue-400 focus:ring-blue-100'}`;
}